import React from "react";
import { Link, Navigate } from "react-router-dom";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { SOLUTIONS } from "@/lib/demoCatalog";
import { Loader2, PlayCircle, ArrowRight, ShieldCheck, Eye } from "lucide-react";

export default function DemoCatalog() {
  const config = usePublicConfig();
  if (config === null) return <Centered><Loader2 className="h-5 w-5 animate-spin" /></Centered>;
  if (!config?.demo?.enabled) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="demo-catalog">
      <DemoHeader />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1" data-testid="sample-data-badge">
          <ShieldCheck className="h-3.5 w-3.5" /> SAMPLE DATA — demonstration environment
        </span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-ink mt-4">Explore GovFlowState by solution</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">Six connected solutions, one platform. Start with the flagship end-to-end lifecycle, or jump into any solution. Everything below runs on clearly-labeled demonstration data.</p>

        <Link to="/demo/legislative-lifecycle" data-testid="flagship-cta"
          className="mt-6 flex items-center gap-4 rounded-2xl bg-brand-navy text-white p-5 hover:bg-brand-navy-900 transition-colors">
          <PlayCircle className="h-8 w-8 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg">Flagship demo: Park Security Camera System Contract → Public Record</div>
            <div className="text-sm text-white/70">A guided 12-stage, 10–15 minute interactive walkthrough across all six solutions.</div>
          </div>
          <ArrowRight className="h-5 w-5" />
        </Link>

        <Link to="/demo/legislative-lifecycle?mode=completed" data-testid="completed-example-cta"
          className="mt-4 flex items-center gap-4 rounded-2xl border border-brand-line bg-white p-5 hover:border-brand-civic transition-colors">
          <Eye className="h-8 w-8 shrink-0 text-brand-teal" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg text-brand-ink">Completed Legislative Example</div>
            <div className="text-sm text-slate-500">Inspect a finished, read-only scenario — published agenda, official vote, decision, minutes and public record.</div>
          </div>
          <ArrowRight className="h-5 w-5 text-brand-civic" />
        </Link>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {SOLUTIONS.map((s) => (
            <div key={s.key} data-testid={`solution-${s.key}`} className="rounded-xl border border-brand-line bg-white p-5">
              <div className="font-display font-bold text-brand-ink">{s.name}</div>
              <p className="text-sm text-slate-500 mt-1">{s.tagline}</p>
              <p className="text-xs text-slate-400 mt-3">Buyer: {s.buyer}</p>
              <p className="text-xs text-slate-400">Reuses: {s.reuses}</p>
              <Link to={s.flagship ? "/demo/legislative-lifecycle" : "/login"} data-testid={`solution-cta-${s.key}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-civic hover:underline">
                {s.flagship ? "Open guided demo" : "Sign in to explore"} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoHeader() {
  return (
    <header className="h-14 bg-white border-b border-brand-line flex items-center px-6">
      <Link to="/" className="font-display font-extrabold text-brand-ink">GovFlowState <span className="text-xs font-semibold text-slate-400">Demo</span></Link>
      <Link to="/login" className="ml-auto text-sm font-semibold text-brand-navy hover:underline">Sign In</Link>
    </header>
  );
}
function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-slate-500">{children}</div>;
}
